// /////////responsive
import React, { useContext, useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Toolbar,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Button,
} from "@mui/material";
import {
  Home,
  ShoppingCart,
  Restaurant,
  People,
  Receipt,
  BarChart,
  PieChart,
  Settings,
  AccountCircle,
  ChevronRight,
  Menu,
  Refresh,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContextProvider";
import { getActiveDailySessionData } from "../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";

// ✨ استيراد rolePages من ملف خارجي (مثلاً roles.js)
import { rolePages } from "../../roles";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { mode } = useContext(ThemeContext);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [sessionDate, setSessionDate] = useState(null);

  // ✅ جلب بيانات المستخدم
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.type;
  const allowedPages = rolePages[role] || [];

  // ✅ جلب تاريخ اليومية الحالية
  useEffect(() => {
    const fetchSessionDate = async () => {
      try {
        const branchId = localStorage.getItem("branch_id");

        if (!branchId) {
          console.warn("⚠️ لا يوجد branch_id في localStorage");
          return;
        }

        const resp = await getActiveDailySessionData(branchId);

        // ✅ التحقق من الاستجابة - الكائن يأتي مباشرة أو داخل data
        if (resp && resp.session_date) {
          setSessionDate(resp.session_date);
        } else if (resp && resp.data && resp.data.session_date) {
          setSessionDate(resp.data.session_date);
        }
      } catch (err) {
        console.error("❌ فشل في جلب تاريخ اليومية:", err);
      }
    };

    if (isMobile) {
      fetchSessionDate();
    }
  }, [isMobile]);

  // ✅ إذا لم يكن هاتفاً، لا تعرض Sidebar
  if (!isMobile) {
    return null;
  }

  // ✅ قائمة الصفحات المتاحة (كل الصفحات المحتملة)
  const menuItems = [
    { text: "لوحة التحكم", icon: <Home />, path: "/dashboard" },
    { text: "شاشة البيع", icon: <ShoppingCart />, path: "/pos" },
    { text: "إدارة القائمة", icon: <Restaurant />, path: "/menu" },
    { text: "الحسابات", icon: <ShoppingCart />, path: "/accounts" },
    { text: "إدارة العملاء", icon: <People />, path: "/customers" },
    { text: "إدارة الفواتير", icon: <Receipt />, path: "/invoices" },
    { text: "التقارير المالية", icon: <BarChart />, path: "/reports" },
    { text: "الإحصائيات", icon: <PieChart />, path: "/Statics" },
    { text: "إدارة الموظفين", icon: <People />, path: "/staff" },
    { text: "إدارة المستخدمين", icon: <AccountCircle />, path: "/users" },
    { text: "الإعدادات", icon: <Settings />, path: "/settings" },
  ];

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="right"
      open={isOpen || !isMobile}
      onClose={toggleSidebar}
      ModalProps={{
        keepMounted: true,
        container: document.body,
        style: { zIndex: 1200 },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: isOpen || isMobile ? 220 : 60,
          transition: "width 0.3s ease-in-out",
          overflowY: "auto",
          direction: "rtl",
          backgroundColor: mode === "dark" ? "#1F1D2B" : "#fff",
          color: mode === "dark" ? "#fff" : "#000",
          height: "100vh",
          position: "fixed",
          top: 0,
          right: 0,
          borderLeft: "2px solid #EA7C69",
          boxShadow: isMobile ? "0px 4px 10px rgba(0,0,0,0.2)" : "none",
          zIndex: 1200,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
      }}
    >
      {/* ✅ زرار الطي/الفتح */}
      <Toolbar sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <IconButton onClick={toggleSidebar} sx={{ color: "#EA7C69" }}>
          {isOpen || isMobile ? <ChevronRight /> : <Menu />}
        </IconButton>
      </Toolbar>
      <Divider />

      {/* ✅ عرض تاريخ اليومية الحالية */}
      {(isOpen || isMobile) && sessionDate && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            py: 1.5,
            px: 2,
            backgroundColor: mode === "dark" ? "#2a2838" : "#f5f5f5",
            borderBottom: "1px solid #EA7C69",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "#fff" : "#000",
              fontWeight: 600,
              fontSize: "13px",
              whiteSpace: "nowrap",
            }}
          >
            📅 تاريخ اليومية: {sessionDate}
          </Typography>
        </Box>
      )}

      {/* ✅ عرض رسالة في حالة عدم وجود يومية نشطة */}
      {(isOpen || isMobile) && !sessionDate && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            py: 1,
            px: 2,
            backgroundColor: mode === "dark" ? "#2a2838" : "#fff3cd",
            borderBottom: "1px solid #EA7C69",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "#ffc107" : "#856404",
              fontWeight: 500,
              fontSize: "11px",
            }}
          >
            ⚠️ لا توجد يومية نشطة
          </Typography>
        </Box>
      )}

      {/* ✅ القائمة */}
      <List
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {menuItems
          .filter((item) => allowedPages.includes(item.path)) // ✨ فلترة حسب rolePages
          .map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem
                button
                key={index}
                component={Link}
                to={item.path}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: isOpen || isMobile ? "flex-start" : "center",
                  width: "100%",
                  padding: "12px",
                  transition: "all 0.3s ease-in-out",
                  borderRadius: isActive ? "15px" : "0px",
                  backgroundColor: isActive ? "#181818" : "transparent",
                  boxShadow: isActive
                    ? "5px 5px 15px rgba(0, 0, 0, 0.3)"
                    : "none",
                  "&:hover": {
                    backgroundColor: "#EA7C69",
                    transform: "scale(1.05)",
                  },
                }}
                onClick={isMobile ? toggleSidebar : undefined}
              >
                {(isOpen || isMobile) && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      color: isActive
                        ? "#fff"
                        : mode === "dark"
                        ? "#fff"
                        : "#000",
                      fontSize: "14px",
                      textAlign: "right",
                      flexGrow: 1,
                    }}
                  />
                )}
                <ListItemIcon
                  sx={{
                    color: isActive
                      ? "#EA7C69"
                      : mode === "dark"
                      ? "#fff"
                      : "#000",
                    minWidth: "40px",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </ListItem>
            );
          })}
      </List>

      {/* زر الريفريش في الأسفل */}
      <Box sx={{ width: "100%", p: 2, mt: "auto" }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<Refresh />}
          onClick={() => window.location.reload()}
        >
          تحديث الصفحة
        </Button>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
